/**
 * Frontend logger (browser-first).
 *
 * - **Browser**: `info` is suppressed in production except warn/error/debug (debug
 *   only in development). All output goes to the devtools console.
 * - **Non-browser contexts** (e.g. Vitest / tooling where `window` is undefined): in
 *   production, structured JSON lines can be written to stdout when Node streams exist
 *   (useful for scripts); otherwise messages fall back to `console`.
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.error('Failed to load', error);
 */

const isServer = typeof window === "undefined";

function timestamp(): string {
  return new Date().toISOString();
}

function formatArgs(args: unknown[]): string {
  return args
    .map((a) => (typeof a === "object" && a !== null ? JSON.stringify(a) : String(a)))
    .join(" ");
}

function formatMessage(level: string, args: unknown[]): string {
  const ts = isServer ? `${timestamp()} ` : "";
  const prefix = isServer ? "[server] " : "[client] ";
  const rest = formatArgs(args);
  return `${ts}${prefix}[${level}] ${rest}`;
}

function writeServerProduction(level: "info" | "warning" | "error" | "debug", args: unknown[]): void {
  const line =
    JSON.stringify({
      time: timestamp(),
      level,
      message: `[server] ${formatArgs(args)}`,
      name: "frontend.server",
    }) + "\n";
  const stdout = typeof process !== "undefined" ? process.stdout : undefined;
  if (stdout && typeof stdout.write === "function") {
    stdout.write(line);
  } else {
    // Edge or minimal runtimes without Node streams
    console.log(line.trimEnd());
  }
}

function emitServer(level: "INFO" | "WARN" | "ERROR" | "DEBUG", args: unknown[]): void {
  if (import.meta.env.PROD) {
    const map = { INFO: "info", WARN: "warning", ERROR: "error", DEBUG: "debug" } as const;
    writeServerProduction(map[level], args);
    return;
  }
  const msg = formatMessage(level, args);
  if (level === "ERROR") {
    console.error(msg);
  } else if (level === "WARN") {
    console.warn(msg);
  } else {
    console.log(msg);
  }
}

export const logger = {
  info(...args: unknown[]): void {
    if (isServer) {
      emitServer("INFO", args);
      return;
    }
    if (import.meta.env.DEV) {
      console.log(formatMessage("INFO", args));
    }
  },

  warn(...args: unknown[]): void {
    if (isServer) {
      emitServer("WARN", args);
      return;
    }
    console.warn(formatMessage("WARN", args));
  },

  error(...args: unknown[]): void {
    if (isServer) {
      emitServer("ERROR", args);
      return;
    }
    console.error(formatMessage("ERROR", args));
  },

  debug(...args: unknown[]): void {
    if (!import.meta.env.DEV) return;
    if (isServer) {
      emitServer("DEBUG", args);
      return;
    }
    console.log(formatMessage("DEBUG", args));
  },
};
